/* eslint-disable no-magic-numbers */

import { format, subDays } from 'date-fns';

export enum DayNumber {
  'MONDAY' = 1,
  'TUESDAY' = 2,
  'WEDNESDAY' = 3,
  'THURSDAY' = 4,
  'FRIDAY' = 5,
  'SATURDAY' = 6,
  'SUNDAY' = 0
}

/**
 * Converts a DD/MM/YYYY date string to YYYY-MM-DD
 * @param date - the string to process
 */
export const convertDDMMYYYtoYYYYMMDD = (date: string) => {
  if (/^\d\d\/\d\d\/\d\d\d\d$/.test(date.trim())) {
    const parts = date.split('/');
    return [parts[2], parts[1], parts[0]].join('-');
  }
};

/**
 * Converts a date string like `2020-04` to a local date offset against the browsers timezone.
 * This is required as JS assumes date strings without timezones are UTC and converts to local.
 * E.g. in a -0300 timezone new Date('2020-04') will return 2020-03-30 21:00 -0300
 * This function would return 2020-04-01 00:00 -0300
 */
export function forceLocalDate(date: string) {
  if (!date) {
    return new Date();
  }
  const [year, month, day = '1'] = date.split('-');
  const localDate = new Date(date);
  localDate.setFullYear(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  localDate.setHours(0, 0, 0);
  return localDate;
}

/**
 * Returns the date one calendar day before the provided date.
 * @param date - Date formatted as a date string.
 */
export const getDateMinusOneDay = (date: string) =>
  format(subDays(forceLocalDate(date), 1), 'yyyy-MM-dd');

export const getDateWithStartOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const getDateWithEndOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const getLast12Months = () => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  const d = new Date();
  d.setDate(1);
  const months = [];
  for (let i = 0; i < 12; i++) {
    months.unshift(monthNames[d.getMonth()]);
    d.setMonth(d.getMonth() - 1);
  }
  return months;
};

export const getMonthsBetweenDates = (startDate: string, endDate: string) => {
  const start = startDate.split('-');
  const end = endDate.split('-');
  const startYear = parseInt(start[0], 10);
  const endYear = parseInt(end[0], 10);
  const dates = [];

  for (let i = startYear; i <= endYear; i++) {
    const endMonth = i != endYear ? 11 : parseInt(end[1], 10) - 1;
    const startMon = i === startYear ? parseInt(start[1], 10) - 1 : 0;
    for (let j = startMon; j <= endMonth; j = j > 12 ? j % 12 || 11 : j + 1) {
      const month = j + 1;
      const displayMonth = month < 10 ? `0${month}` : month;
      dates.push([i, displayMonth, '01'].join('-'));
    }
  }
  return dates;
};

/**
 * Returns all of the monthly dates from the given date, for the given amount of years
 * @param yearNum - The amount of years to generate dates for. Defaults to 3
 * @param currentDate - the date to start from. Defaults to the current date
 */
export const getDatesBetweenTodayAndNYears = (yearNum = 3, currentDate = new Date()) => {
  const startYear = currentDate.getFullYear();
  const endYear = currentDate.getFullYear() + yearNum;

  // const startDate = new Date(startYear, currentDate.getMonth(), currentDate.getDate()).toISOString().split('T')[0];
  // const endDate = new Date(endYear, currentDate.getMonth(), currentDate.getDate()).toISOString().split('T')[0];
  const startDateStr = `${startYear}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
  const endDateStr = `${endYear}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;

  return getMonthsBetweenDates(startDateStr, endDateStr);
};

export const getPreviousDaysInMonth = (date: string) => {
  const currentDay = new Date(date).getDate();
  return Array.from({ length: currentDay - 1 }, (_, i) => `${date.substring(0, 7)}-${i + 1 < 10 ? '0' : ''}${i + 1}`);
};

/**
 * Based on the day number, determines date suffix (1st, 2nd, 3th, 4th etc)
 * @param d day in month
 * @returns
 */
export const nth = (d: number) => {
  if (d > 3 && d < 21) {
    return 'th';
  }
  switch (d % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Returns number of days in specific month
 * @param month month number (1 for January, 2 for February etc)
 */
export const getNumberOfDaysInMonth = (month: number) => {
  const currentYear = new Date().getFullYear();
  const lastDayOfMonth = new Date(currentYear, month, 0);
  return lastDayOfMonth.getDate();
};

/**
 * Returns time in day in hh:mm format
 * @param date date
 */
export const getHoursMinutes = (date: string) => {
  const timezoneDate = new Date(date);
  const hours = timezoneDate.getHours();
  const minutes = timezoneDate.getMinutes();
  const time = `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
  return time;
};

/**
 * Returns the closest date in the future which is the given day (E.G: Monday)
 * @param day - The day the future date should start from
 * @param startDate - The date to start from. Defaults to todays date.
 */
export const getFollowingDayDate = (day: DayNumber, startDate = new Date()) => {
  const newDate = new Date(startDate.valueOf());

  for (let i = 0; i <= 6; i++) {
    newDate.setDate(newDate.getDate() + 1);
    if (newDate.getDay() === day) {
      break;
    }
  }

  return newDate;
};
